/**
 * OccupationalExam List Page
 * List and manage occupational exam catalog
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  GroupWork as ApplicationIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  AlertBadge,
  generateApplicationAlerts,
  DeleteWithDependenciesDialog,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { occupationalExamService } from '../../services/occupationalExamService';
import {
  OccupationalExam,
  OccupationalExamDependencies,
  OccupationalExamFilters,
  ReportTypeLabels,
} from '../../types/occupationalExam';
import { dependencyItems } from '../../utils/dependencyItems';
import { ExamTypeChips } from './components/ExamTypeChips';
import { OccupationalExamFiltersComponent, ExamFiltersData } from './components/OccupationalExamFilters';
import { useDeleteWithDependencies } from '../../hooks/useDeleteWithDependencies';

const MAX_VISIBLE_REPORTS = 2;

const REPORT_KEYS: Array<{ key: keyof OccupationalExam; label: string }> = [
  { key: 'eSocial', label: 'eSocial' },
  { key: 'PPP', label: 'PPP' },
  { key: 'ASO', label: 'ASO' },
  { key: 'PPR', label: 'PPR' },
  { key: 'PCMSO', label: 'PCMSO' },
  { key: 'analyticalReport', label: 'Rel. Analítico' },
  { key: 'PCA', label: 'PCA' },
];

const getExamAlerts = (exam: OccupationalExam) => {
  if (!exam._counts) return [];
  return generateApplicationAlerts({
    companyCount: exam._counts.companies,
    departmentCount: exam._counts.departments,
    occupationCount: exam._counts.occupations,
  });
};

const columns: DataTableColumn<OccupationalExam>[] = [
  {
    id: 'exam',
    label: 'Exame',
    primary: true,
    render: (exam) => {
      const codeDisplay = exam.tussCode ?? exam.esocialCode;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {exam.examName}
            </Typography>
            {codeDisplay && (
              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                {exam.tussCode ? `TUSS: ${exam.tussCode}` : `eSocial: ${exam.esocialCode}`}
              </Typography>
            )}
          </Box>
          <AlertBadge alerts={getExamAlerts(exam)} />
        </Box>
      );
    },
  },
  {
    id: 'types',
    label: 'Tipos',
    render: (exam) => <ExamTypeChips exam={exam} />,
  },
  {
    id: 'reports',
    label: 'Relatórios',
    render: (exam) => {
      const activeReports = REPORT_KEYS.filter((r) => exam[r.key] === true);
      const visibleReports = activeReports.slice(0, MAX_VISIBLE_REPORTS);
      const overflowReports = activeReports.slice(MAX_VISIBLE_REPORTS);

      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', alignItems: 'center' }}>
          {activeReports.length > 0 ? (
            <>
              {visibleReports.map((r) => (
                <Chip key={r.key} label={r.label} size="small" color="primary" variant="outlined" />
              ))}
              {overflowReports.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      {activeReports.map((r) => (
                        <Typography key={r.key} variant="body2">
                          {ReportTypeLabels[r.key] ?? r.label}
                        </Typography>
                      ))}
                    </Box>
                  }
                >
                  <Chip label={`+${overflowReports.length}`} size="small" variant="outlined" />
                </Tooltip>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Nenhum
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    render: (exam) => (
      <Chip
        label={exam.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
        size="small"
        color={exam.status === 'ACTIVE' ? 'success' : 'default'}
      />
    ),
  },
];

export const OccupationalExamListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/occupational-exams/:id/edit', pode('occupational_exam.update'), 'Editar');
  const excluir = useAcao('occupational_exam.delete', 'Excluir');
  const [exams, setExams] = useState<OccupationalExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<OccupationalExamFilters>({});

  const loadExams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      };

      const response = await occupationalExamService.getOccupationalExams(params);
      setExams(response.exams);
      setTotal(response.total);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message ?? 'Erro ao carregar exames ocupacionais');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  // Convert compact filter UI data → API filters
  const handleFilter = useCallback((data: ExamFiltersData) => {
    const filters: OccupationalExamFilters = {};

    if (data.searchTerm) filters.search = data.searchTerm;
    if (data.status) filters.status = data.status as OccupationalExamFilters['status'];
    if (data.examTypes.length === 1) filters.examType = data.examTypes[0] as OccupationalExamFilters['examType'];
    if (data.workTypes.length === 1) filters.workType = data.workTypes[0] as OccupationalExamFilters['workType'];

    // Report type booleans
    if (data.reportTypes.includes('eSocial')) filters.includeESocial = true;
    if (data.reportTypes.includes('PPP')) filters.includePPP = true;
    if (data.reportTypes.includes('ASO')) filters.includeASO = true;

    setApiFilters(filters);
    setPage(0);
  }, []);

  const { deleteTarget, deleteDependencies, handleDeleteClick, closeDeleteDialog, handleDeleteConfirm } =
    useDeleteWithDependencies<OccupationalExam, OccupationalExamDependencies>({
      checkDependencies: (id) => occupationalExamService.checkDependencies(id),
      deleteEntity: (id) => occupationalExamService.deleteOccupationalExam(id),
      checkErrorMessage: 'Erro ao verificar vínculos do exame ocupacional',
      deleteErrorMessage: 'Erro ao excluir exame ocupacional',
      onError: setError,
      onDeleted: loadExams,
    });

  const handleEdit = (exam: OccupationalExam) => {
    navigate(`/occupational-exams/${exam.id}/edit`);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Medicina', path: '/medicina' },
        { label: 'Exames Ocupacionais' },
      ]}
      title="Catálogo de Exames Ocupacionais"
      subtitle="Gerencie o catálogo de exames ocupacionais"
      actions={
        <>
          <RouteActionButton
            para="/exam-applications"
            exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
            variant="outlined"
            color="secondary"
            startIcon={<ApplicationIcon />}
          >
            Aplicar Exames
          </RouteActionButton>
          <RouteActionButton
            para="/occupational-exams/new"
            exige={pode('occupational_exam.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Exame
          </RouteActionButton>
        </>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters - compact inline (no Card wrapper) */}
      <Box sx={{ mb: 3 }}>
        <OccupationalExamFiltersComponent
          onFilter={handleFilter}
          loading={loading}
          total={total}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={exams}
        getRowId={(exam) => exam.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum exame ocupacional encontrado',
          description: 'Esta lista vem do cadastro de exames ocupacionais, onde cada exame guarda o código eSocial, o código TUSS e a periodicidade.',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      <DeleteWithDependenciesDialog
        open={Boolean(deleteTarget)}
        loading={deleteDependencies === null}
        blocked={Boolean(deleteDependencies?.hasApplications)}
        title="Excluir exame ocupacional"
        confirmText="Tem certeza que deseja excluir este exame ocupacional?"
        prerequisite={{
          title: 'Este exame está em uso',
          reason: 'Exames com aplicações ativas não podem ser excluídos — remova as aplicações primeiro.',
          items: dependencyItems(deleteDependencies?.details),
          resolve: {
            label: 'Ver aplicações',
            permission: 'occupational_exam.update',
            mode: 'navigate',
            route: `/exam-applications?examId=${deleteTarget?.id}`,
          },
          deniedHint: 'Peça a um administrador para remover as aplicações deste exame antes de excluí-lo.',
        }}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />
    </PageShell>
  );
};

export default OccupationalExamListPage;
